import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Clock, Eye, Check, CheckCircle2, Maximize2, Minimize2,
  Lightbulb, PlayCircle, ChevronRight, Inbox
} from 'lucide-react';
import {
  porSlug,
  rutaDe,
  tutorialesDeRuta,
  leerProgreso,
  actualizarProgreso,
  estadoDe,
  progresoDeRuta,
  cargarYouTubeAPI
} from './tutorialesData';
import './tutoriales22.scss';

// ==========================================================
// 22a · Reproductor de tutorial
// ==========================================================
const TutorialPlayer = ({ slug }) => {
  const navigate = useNavigate();
  const tutorial = porSlug(slug);

  const [progreso, setProgreso] = useState(() => leerProgreso());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [modo, setModo] = useState('cargando'); // 'cargando' | 'api' | 'iframe'

  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const intervaloRef = useRef(null);

  const guardar = useCallback((parche) => {
    if (!slug) return;
    setProgreso(actualizarProgreso(slug, parche));
  }, [slug]);

  // --- Reproductor de YouTube con seguimiento de avance ---
  useEffect(() => {
    if (!tutorial) return undefined;
    let cancelado = false;
    const host = hostRef.current;

    cargarYouTubeAPI()
      .then((YT) => {
        if (cancelado || !host) return;
        // El nodo que reemplaza YouTube se crea a mano: React no lo gestiona
        // y así no choca con su reconciliación al desmontar.
        const destino = document.createElement('div');
        host.appendChild(destino);
        playerRef.current = new YT.Player(destino, {
          videoId: tutorial.videoId,
          playerVars: { rel: 0, modestbranding: 1 },
          events: {
            onReady: () => !cancelado && setModo('api'),
            onStateChange: (e) => {
              // 1 = reproduciendo, 0 = terminado
              if (e.data === 1) iniciarSeguimiento();
              if (e.data === 0) {
                detenerSeguimiento();
                guardar({ pct: 100, visto: true });
              }
              if (e.data === 2) {
                detenerSeguimiento();
                registrarAvance();
              }
            }
          }
        });
      })
      .catch(() => {
        // Sin API: se usa el <iframe> normal y el avance se marca a mano
        if (!cancelado) setModo('iframe');
      });

    const registrarAvance = () => {
      const p = playerRef.current;
      if (!p || typeof p.getCurrentTime !== 'function') return;
      const dur = p.getDuration ? p.getDuration() : 0;
      if (!dur) return;
      const pct = Math.round((p.getCurrentTime() / dur) * 100);
      if (pct > 0) guardar({ pct, visto: pct >= 95 });
    };

    const iniciarSeguimiento = () => {
      detenerSeguimiento();
      intervaloRef.current = setInterval(registrarAvance, 5000);
    };

    const detenerSeguimiento = () => {
      if (intervaloRef.current) {
        clearInterval(intervaloRef.current);
        intervaloRef.current = null;
      }
    };

    return () => {
      cancelado = true;
      detenerSeguimiento();
      registrarAvance();
      try {
        if (playerRef.current && typeof playerRef.current.destroy === 'function') {
          playerRef.current.destroy();
        }
      } catch (e) {
        /* el iframe ya no estaba en el DOM */
      }
      playerRef.current = null;
      if (host) host.innerHTML = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorial?.slug]);

  // Esc sale de pantalla completa
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  if (!tutorial) {
    return (
      <div className="tut22">
        <div className="tut22-empty" style={{ marginTop: 24 }}>
          <div className="tut22-empty-ic"><Inbox size={22} /></div>
          <h4>Ese tutorial no existe</h4>
          <p>Puede que se haya movido o que el enlace esté mal escrito.</p>
          <div style={{ marginTop: 14 }}>
            <button className="tut22-btn" onClick={() => navigate('/tutoriales')}>
              <ArrowLeft size={15} /> Volver a Tutoriales
            </button>
          </div>
        </div>
      </div>
    );
  }

  const ruta = rutaDe(tutorial.slug);
  const videosRuta = tutorialesDeRuta(ruta);
  const indice = videosRuta.findIndex((t) => t.slug === tutorial.slug);
  const siguiente = indice >= 0 ? videosRuta[indice + 1] : null;
  const avanceRuta = ruta ? progresoDeRuta(progreso, ruta) : null;
  const estado = estadoDe(progreso, tutorial.slug);
  const visto = estado === 'visto';

  const marcarVisto = () => guardar({ visto: !visto, pct: visto ? 0 : 100 });

  return (
    <div className="tut22">
      {/* ── Barra de contexto ────────────────────────────── */}
      <div className="tut22-crumbs">
        <button className="tut22-btn" onClick={() => navigate('/tutoriales')}>
          <ArrowLeft size={16} />
          Tutoriales
        </button>
        <span className="tut22-crumb-txt">
          {tutorial.category} · <b>{tutorial.title}</b>
        </span>
        <span className="tut22-spacer" />
        {avanceRuta && (
          <span className="tut22-crumb-prog">
            <CheckCircle2 size={16} />
            {avanceRuta.hechos} de {avanceRuta.total} completados en esta ruta
          </span>
        )}
      </div>

      <div className="tut22-player-grid">
        {/* ── Columna principal ──────────────────────────── */}
        <div className="tut22-col">
          <div className={`tut22-video ${isFullscreen ? 'is-fullscreen' : ''}`}>
            <div className="tut22-video-frame">
              {/* Aquí monta la API de YouTube su reproductor (y con él, el avance real) */}
              <div className="tut22-video-host" ref={hostRef} />
              {modo === 'cargando' && <div className="tut22-video-loading">Cargando reproductor…</div>}
              {modo === 'iframe' && (
                <iframe
                  src={`https://www.youtube.com/embed/${tutorial.videoId}?rel=0`}
                  title={tutorial.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>

            <span className="tut22-video-badge">
              {indice >= 0 ? 'Video ' + (indice + 1) + (tutorial.duration ? ' · ' : '') : ''}
              {tutorial.duration}
            </span>

            <div className="tut22-video-tools">
              <button
                onClick={() => setIsFullscreen((v) => !v)}
                title={isFullscreen ? 'Salir de pantalla completa (Esc)' : 'Pantalla completa'}
              >
                {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
            </div>
          </div>

          {/* Ficha del tutorial */}
          <div className="tut22-ficha">
            <div className="tut22-ficha-top">
              <div className="tut22-ficha-main">
                <div className="tut22-eyebrow">{tutorial.category.toUpperCase()}</div>
                <h1 className="tut22-ficha-title">{tutorial.title}</h1>
                <div className="tut22-ficha-meta">
                  {tutorial.duration && <span><Clock size={15} />{tutorial.duration}</span>}
                  {tutorial.views != null && <span><Eye size={15} />{tutorial.views.toLocaleString('es-PE')} visualizaciones</span>}
                  <span className="tut22-nivel">Nivel {tutorial.level.toLowerCase()}</span>
                </div>
              </div>

              <div className="tut22-ficha-actions">
                <button
                  className={`tut22-btn ${visto ? '' : 'tut22-btn-primary'}`}
                  onClick={marcarVisto}
                >
                  <Check size={16} />
                  {visto ? 'Visto' : 'Marcar como visto'}
                </button>
              </div>
            </div>

            <hr className="tut22-sep" />

            <div className="tut22-label">DESCRIPCIÓN</div>
            <p className="tut22-desc">{tutorial.description}</p>

            {tutorial.tip && (
              <div className="tut22-tip">
                <Lightbulb size={17} />
                <p>{tutorial.tip}</p>
              </div>
            )}
          </div>
        </div>

        {/* ── Columna derecha ────────────────────────────── */}
        <div className="tut22-col">
          {tutorial.learningPoints?.length > 0 && (
            <div className="tut22-panel">
              <div className="tut22-panel-head is-teal">LO QUE APRENDERÁS</div>
              <div className="tut22-learn">
                {tutorial.learningPoints.map((punto, i) => (
                  <div key={punto}>
                    <b>{i + 1}</b>
                    <span>{punto}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ruta && videosRuta.length > 0 && (
            <div className="tut22-panel">
              <div className="tut22-panel-head with-badge">
                <span>RUTA: {ruta.nombre.toUpperCase()}</span>
                <span className="tut22-panel-badge">
                  {avanceRuta.hechos}/{avanceRuta.total}
                </span>
              </div>

              <div>
                {videosRuta.map((t, i) => {
                  const actual = t.slug === tutorial.slug;
                  const est = estadoDe(progreso, t.slug);
                  return (
                    <button
                      key={t.slug}
                      className={`tut22-ruta-item ${actual ? 'is-current' : ''} ${
                        est === 'visto' ? 'is-done' : ''
                      }`}
                      onClick={() => !actual && navigate(`/tutoriales/${t.slug}`)}
                      disabled={actual}
                    >
                      {est === 'visto' && !actual ? <CheckCircle2 size={18} /> : <PlayCircle size={18} />}
                      <div>
                        <b>{t.title}</b>
                        <small>
                          {actual
                            ? ['Reproduciendo', t.duration].filter(Boolean).join(' · ')
                            : [i === indice + 1 ? 'Siguiente' : '', t.duration, t.level].filter(Boolean).join(' · ')}
                        </small>
                      </div>
                      {!actual && <ChevronRight size={17} />}
                    </button>
                  );
                })}
              </div>

              {siguiente && (
                <div className="tut22-panel-foot">
                  <button
                    className="tut22-btn tut22-btn-primary"
                    onClick={() => navigate(`/tutoriales/${siguiente.slug}`)}
                  >
                    Siguiente tutorial
                    <ArrowRight size={16} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Ruta genérica /tutoriales/:slug
export const TutorialPorSlug = () => {
  const { slug } = useParams();
  return <TutorialPlayer slug={slug} />;
};

// Se conservan los tres componentes con nombre por las rutas y el submenú del sidebar
export const TutorialIntroduccionLicitaHub = () => <TutorialPlayer slug="introduccion-licitahub" />;
export const TutorialProcesosMenores8UIT = () => <TutorialPlayer slug="procesos-menores-8-uit" />;
export const TutorialBusquedaAvanzadaSEACE = () => <TutorialPlayer slug="busqueda-avanzada-seace" />;

export default TutorialPlayer;
